/**
 * Google AdSense — web ads service.
 *
 * Mirrors the role of `admob.service.js` on native: loads the ad SDK and
 * triggers rendering of pre-placed slots. Runs only when:
 *   1. The platform is a browser (NOT Capacitor native — that uses AdMob).
 *   2. `WebAds.publisherId` is configured (otherwise no script loads).
 *
 * Slot rendering model:
 *   `www/index.html` contains `<div class="ad-slot" data-ad-key="top|mid|sticky">`
 *   placeholders. On init we walk them, drop in the AdSense `<ins>` element
 *   with the matching slot ID, and let the AdSense script auto-fill.
 *
 * Empty slots (no slot ID set, or the SDK didn't fill them) collapse via
 * the `.ad-slot:empty` CSS rule — no visible empty boxes.
 */

import { WebAds } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('adsense');

let initialised = false;

/** @returns {boolean} */
function isNative() {
  return !!(/** @type {any} */ (window).Capacitor?.isNativePlatform?.());
}

/** @returns {boolean} */
export function isAvailable() {
  return typeof window !== 'undefined' && !isNative() && !!WebAds.publisherId;
}

/**
 * Ensures the AdSense loader script is present in <head>. In the normal
 * production setup it's already there (static tag in index.html, marker
 * `data-adsense-loaded="1"`), so this is a no-op. For dev / preview
 * environments without the static tag we inject it on the fly.
 */
function injectLoader() {
  if (document.querySelector('script[data-adsense-loaded]')) return;
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(WebAds.publisherId)}`;
  s.setAttribute('data-adsense-loaded', '1');
  document.head.appendChild(s);
}

/**
 * Fill a single `.ad-slot[data-ad-key=...]` element with the AdSense markup
 * for the configured slot ID. No-op if the slot already has content (the
 * `<ins>` tag), or if no slot ID is configured for that key.
 *
 * @param {HTMLElement} el
 */
function renderSlot(el) {
  const key = /** @type {keyof typeof WebAds.slots} */ (el.dataset.adKey || '');
  if (!key) return;
  const slotId = WebAds.slots[key];
  if (!slotId) return;
  if (el.querySelector('ins.adsbygoogle')) return; // already rendered

  const ins = document.createElement('ins');
  ins.className = 'adsbygoogle';
  ins.style.display = 'block';
  ins.setAttribute('data-ad-client', WebAds.publisherId);
  ins.setAttribute('data-ad-slot', slotId);
  ins.setAttribute('data-ad-format', 'auto');
  ins.setAttribute('data-full-width-responsive', 'true');
  el.appendChild(ins);

  try {
    (/** @type {any} */ (window).adsbygoogle = /** @type {any} */ (window).adsbygoogle || []).push({});
  } catch (err) {
    log.warn('adsbygoogle.push failed for slot', key, err);
  }
}

/**
 * Walks the DOM for any `.ad-slot` containers and renders each one. Call
 * this once on bootstrap; future dynamic slots can call it again — the
 * "already rendered" guard inside `renderSlot` makes it idempotent.
 */
export function renderAllSlots() {
  if (!isAvailable()) return;
  /** @type {NodeListOf<HTMLElement>} */
  const slots = document.querySelectorAll('.ad-slot[data-ad-key]');
  slots.forEach((el) => renderSlot(el));
  observeStickyHeight();
}

/**
 * Keep `--ad-banner-h` in sync with the actual rendered height of the
 * sticky bottom ad slot, so layout overrides that reserve bottom space
 * (e.g. the login-container padding-bottom) match the real banner size.
 * Mirrors what admob.service.js does on native via `bannerAdSizeChanged`.
 *
 * Idempotent — only attaches a single ResizeObserver per page load.
 */
let stickyObserved = false;
function observeStickyHeight() {
  if (stickyObserved) return;
  const sticky = /** @type {HTMLElement|null} */ (
    document.querySelector('.ad-slot-sticky')
  );
  if (!sticky) return;
  stickyObserved = true;
  const apply = () => {
    const h = sticky.offsetHeight || 0;
    document.documentElement.style.setProperty('--ad-banner-h', h + 'px');
  };
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(apply).observe(sticky);
  }
  // First pass after the AdSense iframe has had a moment to paint. The
  // observer covers later size changes; this catches the initial render.
  setTimeout(apply, 1200);
}

/**
 * One-time initialisation. Loads the SDK + renders all currently-placed
 * slots. Safe to call multiple times.
 *
 * @returns {Promise<boolean>} true if AdSense was initialised, false if the
 *   platform / config makes it inapplicable.
 */
export async function init() {
  if (initialised) return true;
  if (!isAvailable()) {
    log.info('adsense skipped (native platform or no publisher ID configured)');
    return false;
  }
  injectLoader();
  renderAllSlots();
  initialised = true;
  log.info('adsense initialised for', WebAds.publisherId);
  return true;
}
